import React, { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Typography,
} from '@mui/material';
import { NotificationsNone } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  fetchUserNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../../../Redux/Reducers/UserNotificationsReducer';
import {
  isNotificationRead,
  getNotificationId,
  getNotificationRedirectUrl,
} from '../../../utils/notificationUtils';

function NotificationsInbox() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const notificationsState = useSelector(
    (state) => state.userNotifications || {}
  );
  const user = useSelector((state) => state.AuthReducer?.user || state.auth?.user);

  const {
    notifications = [],
    loading,
    pagination = {},
    unreadCount = 0,
  } = notificationsState;

  const type = searchParams.get('type') || 'all';

  const observer = useRef();

  const currentPage = pagination?.page || 1;
  const totalPages = pagination?.totalPages || 1;

  // Initial Fetch
  useEffect(() => {
    dispatch(
      fetchUserNotifications({
        type,
        page: 1,
        limit: 10,
      })
    );
  }, [dispatch, type]);

  // Infinite Scroll
  const lastNotificationRef = useCallback(
    (node) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          currentPage < totalPages
        ) {
          dispatch(
            fetchUserNotifications({
              type,
              page: currentPage + 1,
              limit: 10,
            })
          );
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, currentPage, totalPages, dispatch, type]
  );

  const handleNotificationClick = async (notification) => {
    const notificationId = getNotificationId(notification);

    if (
      notificationId &&
      !isNotificationRead(notification)
    ) {
      try {
        await dispatch(
          markNotificationAsRead({
            id: notificationId,
          })
        ).unwrap();
      } catch (error) {
        console.log(
          'MARK NOTIFICATION READ ERROR:',
          error
        );
      }
    }

    const redirectUrl = getNotificationRedirectUrl(notification, user);

    if (redirectUrl) {
      navigate(redirectUrl);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await dispatch(
        markAllNotificationsAsRead()
      ).unwrap();

      dispatch(
        fetchUserNotifications({
          type,
          page: 1,
          limit: 10,
        })
      );
    } catch (error) {
      console.log(
        'MARK ALL NOTIFICATIONS READ ERROR:',
        error
      );
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 2, md: 4 },
        py: { xs: 2, md: 4 },
      }}
    >
      <Card
        sx={{
          maxWidth: 760,
          mx: 'auto',
          background: '#2e2a32',
          border:
            '1px solid rgba(230, 210, 249, 0.1)',
          boxShadow:
            '0px 10px 40px rgba(0,0,0,0.45)',
          color: '#fff',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 2,
              borderBottom:
                '1px solid rgba(255,255,255,0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                Notifications
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  color:
                    'rgba(255,255,255,0.55)',
                  mt: 0.5,
                }}
              >
                {unreadCount} unread
              </Typography>
            </Box>

            <Button
              type="button"
              onClick={handleMarkAllRead}
              disabled={
                !notifications.length ||
                unreadCount === 0
              }
              sx={{
                minWidth: 'auto',
                p: 0,
                textTransform: 'none',
                color: '#ff69b4',
                fontWeight: 600,
                fontSize: 12,
                '&.Mui-disabled': {
                  color:
                    'rgba(255,255,255,0.3)',
                },
              }}
            >
              Mark all read
            </Button>
          </Box>

          {/* Notifications */}
          <Box
            sx={{
              maxHeight: 620,
              overflowY: 'auto',
            }}
          >
            {notifications.length > 0 ? (
              notifications.map((item, index) => {
                const notificationId =
                  getNotificationId(item);

                const unread =
                  !isNotificationRead(item);

                const title =
                  item?.title ||
                  item?.heading ||
                  item?.subject ||
                  'Notification';

                const description =
                  item?.description ||
                  item?.message ||
                  item?.body ||
                  '';

                const time =
                  item?.time ||
                  item?.createdAt ||
                  item?.created_at ||
                  item?.sentAt ||
                  item?.sent_at ||
                  '';

                const isLastItem =
                  index ===
                  notifications.length - 1;

                return (
                  <MenuItem
                    key={notificationId || index}
                    ref={
                      isLastItem
                        ? lastNotificationRef
                        : null
                    }
                    onClick={() =>
                      handleNotificationClick(item)
                    }
                    sx={{
                      alignItems: 'flex-start',
                      gap: 1.5,
                      px: 2,
                      py: 1.8,
                      borderBottom:
                        '1px solid rgba(255,255,255,0.04)',
                      transition: 'all .2s ease',
                      background: unread
                        ? 'rgba(255,255,255,0.01)'
                        : 'transparent',
                      '&:hover': {
                        background:
                          'rgba(255,255,255,0.05)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '50%',
                        background:
                          'rgba(179, 136, 255, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <NotificationsNone
                        sx={{
                          fontSize: 20,
                          color: '#ff69b4',
                        }}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent:
                            'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#fff',
                            pr: 1,
                          }}
                        >
                          {title}
                        </Typography>

                        {unread && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background:
                                '#ff69b4',
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        sx={{
                          fontSize: 13,
                          color:
                            'rgba(255,255,255,0.65)',
                          mt: 0.4,
                          lineHeight: '20px',
                        }}
                      >
                        {description}
                      </Typography>

                      {time ? (
                        <Typography
                          sx={{
                            fontSize: 12,
                            color:
                              'rgba(255,255,255,0.45)',
                            mt: 1,
                          }}
                        >
                          {time}
                        </Typography>
                      ) : null}
                    </Box>
                  </MenuItem>
                );
              })
            ) : !loading ? (
              <Box
                sx={{
                  px: 2,
                  py: 4,
                  color:
                    'rgba(255,255,255,0.65)',
                }}
              >
                No notifications yet.
              </Box>
            ) : null}

            {/* Loader */}
            {loading && (
              <Box
                sx={{
                  px: 2,
                  py: 3,
                  textAlign: 'center',
                  color:
                    'rgba(255,255,255,0.6)',
                }}
              >
                Loading more notifications...
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default NotificationsInbox;
